import React, { useState, useRef } from 'react';
import {X, Calculator, RotateCcw, Download, Waves} from 'lucide-react';
import html2canvas from 'html2canvas';

interface FlujoNormalTrapecialCalculatorProps {
  onClose: () => void;
}

interface Resultados {
  TirNormal: string;
  Ahid: string;
  Pmojado: string;
  RHid: string;
  EAgua: string;
  Velocidad: string;
  NFroud: string;
  EnergiaE: string;
  Tflujo: string;
}

const initialResults: Resultados = {
  TirNormal: '',
  Ahid: '',
  Pmojado: '',
  RHid: '',
  EAgua: '',
  Velocidad: '',
  NFroud: '',
  EnergiaE: '',
  Tflujo: '',
};

const materiales = [
  { label: 'Concreto (Acabado con llana metálica)', value: '0.013' },
  { label: 'Concreto (Acabado con llana de madera)', value: '0.015' },
  { label: 'Concreto (Acabado, con grava en el fondo)', value: '0.017' },
  { label: 'Concreto (Sin acabado)', value: '0.017' },
  { label: 'Cemento', value: '0.013' },
  { label: 'Metal', value: '0.012' },
  { label: 'Madera', value: '0.012' },
  { label: 'Ladrillo', value: '0.015' },
  { label: 'Mampostería de piedra', value: '0.025' },
  { label: 'Piedra Labrada', value: '0.015' },
  { label: 'Asfalto liso', value: '0.013' },
  { label: 'Asfalto Rugoso', value: '0.016' },
  { label: 'Cubierta Vegetal', value: '0.030' },
  { label: 'Tierra', value: '0.022' },
  { label: 'Hierba densa', value: '0.080' },
  { label: 'Acrílico', value: '0.009' },
  { label: 'PVC', value: '0.009' },
];

const FlujoNormalTrapecialCalculator: React.FC<FlujoNormalTrapecialCalculatorProps> = ({ onClose }) => {
  const [Gasto, setGasto] = useState('');
  const [Ancho, setAncho] = useState('');
  const [Talud, setTalud] = useState('');
  const [Rugosidad, setRugosidad] = useState('');
  const [Pendiente, setPendiente] = useState('');
  const [resultados, setResultados] = useState<Resultados>(initialResults);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const captureRef = useRef<HTMLDivElement>(null);

  const calcular = () => {
    setError('');
    const q = Number(Gasto);
    const b = Number(Ancho);
    const k = Number(Talud);
    const n = Number(Rugosidad);
    const s = Number(Pendiente);

    if (q === 0 || n === 0 || s < 0 || isNaN(q) || isNaN(n) || isNaN(s)) {
      setError('Faltan datos o son incorrectos. Verifique los valores ingresados.');
      return;
    }

    const theta = Math.atan(s);
    let yn: number;
    let an: number;
    let pn: number;
    let rh: number;

    try {
      if (b === 0) {
        // Sección Triangular
        if (k === 0) {
          setError('Para sección triangular el talud (K) no puede ser 0.');
          return;
        }
        yn = Math.pow(
          (q * n * Math.pow(2 * Math.pow(1 + k * k, 0.5), 2 / 3)) /
            (Math.pow(s, 0.5) * Math.pow(k, 5 / 3)),
          3 / 8
        );
        an = k * yn * yn;
        pn = 2 * yn * Math.pow(Math.pow(k, 2) + 1, 0.5);
        rh = an / pn;
      } else {
        // Sección Trapecial / Rectangular - Newton Raphson
        yn = 1;
        const pn1 = (q * n) / Math.pow(s, 0.5);
        let pn2 = 100;
        let iter = 0;
        an = 0;
        pn = 0;
        rh = 0;

        while (Math.abs(pn1 - pn2) >= 0.00000011 && iter < 1000) {
          an = (b + k * yn) * yn;
          pn = b + 2 * yn * Math.pow(Math.pow(k, 2) + 1, 0.5);
          rh = an / pn;
          pn2 = an * Math.pow(rh, 2 / 3);
          const asl = b + 2 * k * yn;
          const pn3 =
            (5 / 3) * asl * Math.pow(Math.pow(rh, 2), 1 / 3) -
            (4 / 3) * Math.pow(rh, 5 / 3) * Math.pow(1 + Math.pow(k, 2), 0.5);
          const ptn = (pn2 - pn1) / pn3;
          yn = yn - ptn;
          iter++;
        }

        if (iter >= 1000 || !isFinite(yn) || yn <= 0) {
          setError('No se logró convergencia. Revise los datos ingresados.');
          return;
        }
      }

      const te = b + 2 * k * yn;
      const vel = q / an;
      const fr = vel / Math.pow((9.81 * an) / te, 0.5);
      const ener = yn * Math.cos(theta) + Math.pow(vel, 2) / (2 * 9.81);
      const tflujo = fr > 1 ? 'Supercrítico' : 'Subcrítico';

      const trunc = (x: number) => (Math.trunc(x * 10000.0) / 10000.0).toString();

      setResultados({
        TirNormal: trunc(yn),
        Ahid: trunc(an),
        Pmojado: trunc(pn),
        RHid: trunc(rh),
        EAgua: trunc(te),
        Velocidad: trunc(vel),
        NFroud: trunc(fr),
        EnergiaE: trunc(ener),
        Tflujo: tflujo,
      });
    } catch (e) {
      setError('Error en el cálculo. Verifique los datos.');
    }
  };

  const limpiar = () => {
    setGasto('');
    setAncho('');
    setTalud('');
    setRugosidad('');
    setPendiente('');
    setResultados(initialResults);
    setError('');
  };

  const exportarPNG = async () => {
    if (!captureRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(captureRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
      });
      const link = document.createElement('a');
      link.download = `flujo-normal-trapecial-openHH-${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('Error exportando PNG:', err);
    } finally {
      setExporting(false);
    }
  };

  const tieneResultados = resultados.TirNormal !== '';

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm overflow-y-auto">
      <div className="min-h-screen flex items-start justify-center p-4 py-8">
        <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full my-auto relative">
          {/* Close button - sticky */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white shadow-lg border border-gray-200 flex items-center justify-center hover:bg-red-50 hover:border-red-200 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5 text-gray-700" />
          </button>

          {/* Capturable area */}
          <div ref={captureRef} className="bg-white rounded-2xl">
            {/* Header */}
            <div className="bg-gradient-to-br from-teal-600 via-emerald-600 to-green-600 px-8 py-6 rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30">
                  <Waves className="w-7 h-7 text-white" />
                </div>
                <div>
                  <p className="text-emerald-100 text-sm font-semibold uppercase tracking-wide">Flujo Normal</p>
                  <h2 className="text-2xl font-bold text-white">Trapecial, Rectangular y Triangular</h2>
                </div>
              </div>
              <p className="text-emerald-50 text-sm mt-3 max-w-3xl">
                Ingrese los datos para realizar el diseño hidráulico de canales trapezoidales, rectangulares o triangulares.
                Si <strong>B = 0</strong>, se calcula como sección triangular.
              </p>
            </div>

            {/* Content */}
            <div className="p-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Inputs */}
              <div className="space-y-5">
                <div className="border-b border-gray-200 pb-2">
                  <h3 className="text-lg font-bold text-amber-600 flex items-center gap-2">
                    <span className="w-1 h-6 bg-amber-500 rounded"></span>
                    Datos a ingresar
                  </h3>
                </div>

                {/* Diagrama dinamico de la seccion del canal */}
                <ChannelSectionDiagram
                  base={Number(Ancho) || 0}
                  talud={Number(Talud) || 0}
                  tirante={Number(resultados.TirNormal) || 0}
                />

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    Q = Gasto del canal (m³/s)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={Gasto}
                    onChange={(e) => setGasto(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                    placeholder="Ej: 5.2"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    B = Ancho de base o plantilla (m) <span className="text-xs text-gray-500">— use 0 para sección triangular</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={Ancho}
                    onChange={(e) => setAncho(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                    placeholder="Ej: 2.0"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    K = Talud (Adimensional) <span className="text-xs text-gray-500">— use 0 para sección rectangular</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={Talud}
                    onChange={(e) => setTalud(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                    placeholder="Ej: 1.5"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    Material del canal <span className="text-xs text-gray-500">(autocompleta n)</span>
                  </label>
                  <select
                    onChange={(e) => setRugosidad(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors bg-white"
                    defaultValue=""
                  >
                    <option value="" disabled>Seleccione un material...</option>
                    {materiales.map((m, i) => (
                      <option key={i} value={m.value}>
                        {m.label} (n = {m.value})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    n = Rugosidad (Adimensional)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.001"
                    value={Rugosidad}
                    onChange={(e) => setRugosidad(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                    placeholder="Ej: 0.013"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    So = Pendiente (m/m)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.0001"
                    value={Pendiente}
                    onChange={(e) => setPendiente(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                    placeholder="Ej: 0.001"
                  />
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                    {error}
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={calcular}
                    className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold py-3 px-4 rounded-lg shadow-md transition-all"
                  >
                    <Calculator className="w-4 h-4" /> Calcular
                  </button>
                  <button
                    onClick={limpiar}
                    className="flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 px-4 rounded-lg transition-colors"
                  >
                    <RotateCcw className="w-4 h-4" /> Limpiar
                  </button>
                </div>
              </div>

              {/* Results */}
              <div className="space-y-3">
                <div className="border-b border-gray-200 pb-2">
                  <h3 className="text-lg font-bold text-amber-600 flex items-center gap-2">
                    <span className="w-1 h-6 bg-amber-500 rounded"></span>
                    Resultados
                  </h3>
                </div>

                {[
                  { label: 'Tirante Normal (m)', value: resultados.TirNormal, primary: true },
                  { label: 'Área Hidráulica (m²)', value: resultados.Ahid },
                  { label: 'Perímetro Mojado (m)', value: resultados.Pmojado },
                  { label: 'Radio Hidráulico (m)', value: resultados.RHid },
                  { label: 'Espejo del Agua (m)', value: resultados.EAgua },
                  { label: 'Velocidad (m/s)', value: resultados.Velocidad },
                  { label: 'Núm. de Froude (Adim.)', value: resultados.NFroud },
                  { label: 'Energía Específica (m)', value: resultados.EnergiaE },
                ].map((item, i) => (
                  <div
                    key={i}
                    className={`flex justify-between items-center p-3 rounded-lg border ${
                      item.primary
                        ? 'bg-emerald-50 border-emerald-200'
                        : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <span className={`text-sm ${item.primary ? 'text-emerald-800 font-semibold' : 'text-gray-700'}`}>
                      {item.label}
                    </span>
                    <span
                      className={`font-mono font-bold ${
                        item.primary ? 'text-emerald-700 text-base' : 'text-gray-900'
                      }`}
                    >
                      {item.value || '—'}
                    </span>
                  </div>
                ))}

                <div
                  className={`flex justify-between items-center p-3 rounded-lg border ${
                    resultados.Tflujo === 'Supercrítico'
                      ? 'bg-orange-50 border-orange-200'
                      : resultados.Tflujo === 'Subcrítico'
                      ? 'bg-blue-50 border-blue-200'
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <span className="text-sm text-gray-700 font-medium">Tipo de flujo</span>
                  <span
                    className={`font-bold text-sm px-3 py-1 rounded-full ${
                      resultados.Tflujo === 'Supercrítico'
                        ? 'bg-orange-200 text-orange-800'
                        : resultados.Tflujo === 'Subcrítico'
                        ? 'bg-blue-200 text-blue-800'
                        : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {resultados.Tflujo || '—'}
                  </span>
                </div>
              </div>
            </div>

            {/* Footer brand */}
            <div className="px-8 py-4 border-t border-gray-100 bg-gradient-to-r from-gray-50 to-emerald-50/30 rounded-b-2xl flex items-center justify-between">
              <p className="text-xs text-gray-500">
                Generado por <span className="font-bold text-emerald-700">openHH</span> · HHTools — {new Date().toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
              <button
                onClick={exportarPNG}
                disabled={!tieneResultados || exporting}
                className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold text-sm py-2 px-4 rounded-lg shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="w-4 h-4" />
                {exporting ? 'Exportando...' : 'Exportar PNG'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Diagrama SVG dinamico de la seccion transversal del canal abierto.
 * Refleja en tiempo real B (base), K (talud) y el tirante (Yn calculado).
 * Conmuta automaticamente entre seccion triangular (B=0), rectangular (K=0) y trapecial.
 */
const ChannelSectionDiagram: React.FC<{ base: number; talud: number; tirante: number }> = ({
  base,
  talud,
  tirante,
}) => {
  const W = 360;
  const H = 200;
  const padX = 50;
  const baseY = H - 45;

  const tipo = base <= 0 ? 'Triangular' : talud <= 0 ? 'Rectangular' : 'Trapecial';

  // Valores de referencia para dibujar de forma estable cuando faltan datos
  const b = base > 0 ? base : 0;
  const k = talud > 0 ? talud : 0;
  const yRef = tirante > 0 ? tirante : 1;

  // Anchos geometricos (en unidades reales)
  const topWidth = b + 2 * k * yRef; // espejo del agua
  const maxWidth = Math.max(topWidth, b, 0.001);

  // Escalas para encajar el dibujo en el viewBox
  const usableW = W - padX * 2;
  const usableH = baseY - 40;
  const scaleX = usableW / maxWidth;
  const scaleY = usableH / yRef;
  const scale = Math.min(scaleX, scaleY);

  const cx = W / 2;
  const bottomHalf = (b * scale) / 2;
  const topHalf = (topWidth * scale) / 2;
  const waterDepth = yRef * scale;
  const waterTopY = baseY - waterDepth;

  // Puntos del canal
  const blX = cx - bottomHalf;
  const brX = cx + bottomHalf;
  const tlX = cx - topHalf;
  const trX = cx + topHalf;

  const fmt = (n: number) => (Number.isFinite(n) ? n.toFixed(2) : '0.00');

  // Trazo de las paredes del canal (margen un poco mas alto que el agua)
  const wallTopY = waterTopY - 16;
  const wallExtra = 16 * (topWidth > b ? (k > 0 ? k : 0) : 0) * scale / (yRef * scale || 1);
  const wallTlX = tlX - (k > 0 ? (16 / (scaleY || 1)) * k * scale : 0);
  const wallTrX = trX + (k > 0 ? (16 / (scaleY || 1)) * k * scale : 0);

  const waterPath = `M ${blX},${baseY} L ${tlX},${waterTopY} L ${trX},${waterTopY} L ${brX},${baseY} Z`;
  const channelPath = `M ${wallTlX},${wallTopY} L ${blX},${baseY} L ${brX},${baseY} L ${wallTrX},${wallTopY}`;

  return (
    <div className="bg-white rounded-lg border border-emerald-200 overflow-hidden shadow-sm">
      <div className="flex items-center justify-between px-3 py-2 bg-emerald-50 border-b border-emerald-100">
        <span className="text-xs font-semibold text-emerald-800">Seccion transversal del canal</span>
        <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-white text-emerald-700 border border-emerald-300">
          {tipo}
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
        <defs>
          <linearGradient id="trapWaterGrad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#34d399" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#0d9488" stopOpacity="0.95" />
          </linearGradient>
        </defs>

        {/* Terreno / paredes del canal */}
        <path d={channelPath} fill="none" stroke="#374151" strokeWidth="3" strokeLinejoin="round" />

        {/* Agua */}
        <path d={waterPath} fill="url(#trapWaterGrad)" stroke="#0f766e" strokeWidth="1" />
        {topHalf > 6 && (
          <path
            d={`M ${tlX},${waterTopY} Q ${cx},${waterTopY - 3} ${trX},${waterTopY}`}
            fill="none"
            stroke="#ffffff"
            strokeWidth="1"
            opacity="0.7"
          />
        )}

        {/* Cota tirante Yn */}
        <g>
          <line x1={brX + 24} y1={waterTopY} x2={brX + 24} y2={baseY} stroke="#dc2626" strokeWidth="1.2" />
          <polygon points={`${brX + 24},${waterTopY} ${brX + 20},${waterTopY + 6} ${brX + 28},${waterTopY + 6}`} fill="#dc2626" />
          <polygon points={`${brX + 24},${baseY} ${brX + 20},${baseY - 6} ${brX + 28},${baseY - 6}`} fill="#dc2626" />
          <text x={brX + 30} y={(waterTopY + baseY) / 2 + 4} fill="#b91c1c" fontSize="11" fontWeight="700">
            {tirante > 0 ? `Yn=${fmt(tirante)}m` : 'Yn'}
          </text>
        </g>

        {/* Cota base B */}
        {b > 0 && (
          <g>
            <line x1={blX} y1={baseY + 16} x2={brX} y2={baseY + 16} stroke="#1d4ed8" strokeWidth="1.2" />
            <polygon points={`${blX},${baseY + 16} ${blX + 6},${baseY + 12} ${blX + 6},${baseY + 20}`} fill="#1d4ed8" />
            <polygon points={`${brX},${baseY + 16} ${brX - 6},${baseY + 12} ${brX - 6},${baseY + 20}`} fill="#1d4ed8" />
            <text x={cx} y={baseY + 30} textAnchor="middle" fill="#1e40af" fontSize="11" fontWeight="700">
              B = {fmt(base)} m
            </text>
          </g>
        )}

        {/* Etiqueta talud K */}
        {k > 0 && tirante >= 0 && (
          <text x={tlX - 6} y={(waterTopY + baseY) / 2} textAnchor="end" fill="#059669" fontSize="10" fontWeight="700">
            K = {fmt(talud)}
          </text>
        )}
      </svg>
    </div>
  );
};

export default FlujoNormalTrapecialCalculator;
